import { z } from '@hono/zod-openapi';
import type { CheckConfig, PhotoStep } from '../types';
import { COMPLETION_MESSAGE_MAX_LENGTH, DEFAULT_COMPLETION_MESSAGES } from '../utils/completion-message';
import { VehicleCategorySchema } from './vehicleCategory';
import { LabelSchema } from './access';

const completionMessageField = (example: string, description: string) =>
  z.string().trim().min(1).max(COMPLETION_MESSAGE_MAX_LENGTH).openapi({
    example,
    description,
  });

const strictVinCodeField = z
  .boolean()
  .optional()
  .openapi({
    example: false,
    description:
      'When true, POST /sessions requires a 4-17 character vinCode without I, O or Q (ISO 3779 charset). ' +
      'When false, any 1-17 Latin letters or digits are accepted, and the photo VIN check is skipped for a 1-3 character code. ' +
      'Omitted on create: false. Omitted on update: unchanged.',
  });

const completionMessageFields = {
  completionMessageEt: completionMessageField(
    DEFAULT_COMPLETION_MESSAGES.completionMessageEt,
    'Estonian text shown under the success message when the capture session is finished. Copied onto the session at creation.'
  ),
  completionMessageEn: completionMessageField(
    DEFAULT_COMPLETION_MESSAGES.completionMessageEn,
    'English text shown under the success message when the capture session is finished. Copied onto the session at creation.'
  ),
  completionMessageRu: completionMessageField(
    DEFAULT_COMPLETION_MESSAGES.completionMessageRu,
    'Russian text shown under the success message when the capture session is finished. Copied onto the session at creation.'
  ),
};

export const CheckConfigSchema = z.object({
  type: z.enum(['gps', 'exif', 'dimensions', 'quality', 'vin', 'is_car']).openapi({
    example: 'gps',
    description: 'Type of validation check to perform',
  }),
  allow_bypass: z.boolean().openapi({
    example: false,
    description: 'Whether this check can be bypassed if it fails',
  }),
}) satisfies z.ZodType<CheckConfig>;

export const PhotoStepSchema = z
  .object({
    id: z.string().openapi({
      example: 'front-diagonal',
      description: 'Unique identifier for this photo step',
    }),
    title_en: z.string().openapi({
      example: 'Front Diagonal View',
      description: 'Display title for this photo step (English)',
    }),
    description_en: z.string().openapi({
      example: 'Capture the vehicle from the front-left corner at a 45-degree angle',
      description: 'Instructions for capturing this photo (English)',
    }),
    title_et: z.string().openapi({
      example: 'Esiosa diagonaalne vaade',
      description: 'Display title for this photo step (Estonian)',
    }),
    description_et: z.string().openapi({
      example: 'Pildistage sõidukit esivasakust nurgast 45-kraadise nurga alt',
      description: 'Instructions for capturing this photo (Estonian)',
    }),
    title_ru: z.string().openapi({
      example: 'Вид передней диагонали',
      description: 'Display title for this photo step (Russian)',
    }),
    description_ru: z.string().openapi({
      example: 'Сфотографируйте автомобиль с переднего левого угла под углом 45 градусов',
      description: 'Instructions for capturing this photo (Russian)',
    }),
    checks: z.array(CheckConfigSchema).openapi({
      example: [
        { type: 'gps', allow_bypass: false },
        { type: 'exif', allow_bypass: false },
      ],
      description: 'List of validation checks to perform on the photo for this step',
    }),
    photoCount: z.number().int().min(0).openapi({
      example: 1,
      description:
        'Number of photos required for this step. Use 0 for a fully optional step (requires maxAdditionalPhotos >= 1).',
    }),
    maxAdditionalPhotos: z.number().int().min(0).max(10).optional().openapi({
      example: 0,
      description:
        'Optional extras beyond photoCount (0–10). Omitted or unset is treated as 0. Total allowed = photoCount + maxAdditionalPhotos.',
    }),
    illustration_url: z.string().url().optional().openapi({
      example: 'https://pub-1234567890abcdef.r2.dev/illustrations/front-view.jpg',
      description: 'Optional R2 URL for step illustration image to display before camera capture',
    }),
  })
  .refine((step) => step.photoCount + (step.maxAdditionalPhotos ?? 0) >= 1, {
    message: 'photoCount + maxAdditionalPhotos must be at least 1',
    path: ['maxAdditionalPhotos'],
  })
  .openapi('PhotoStep') satisfies z.ZodType<PhotoStep>;

export const ConfigurationSchema = z
  .object({
    id: z.string(),
    name: z.string().openapi({
      example: 'registration',
      description: 'Configuration name (e.g., "registration" or "deregistration")',
    }),
    vehicleCategoryId: z.string().openapi({
      example: 'vc_abc123xyz',
      description: 'ID of the vehicle category this configuration belongs to',
    }),
    vehicleCategory: VehicleCategorySchema.optional().openapi({
      description:
        'Joined vehicle category record. Populated by list/detail endpoints and session snapshots; omitted on write.',
    }),
    labels: z
      .array(LabelSchema)
      .optional()
      .openapi({
        example: ['live'],
        description:
          'Labels that decide which admin users and integration tokens can see this configuration. Missing on session snapshots taken before labels existed.',
      }),
    steps: z.array(PhotoStepSchema),
    expiryDuration: z.number().int().openapi({
      example: 180,
      description: 'Session expiry duration in minutes',
    }),
    ...completionMessageFields,
    strictVinCode: strictVinCodeField,
    updatedAt: z.date().or(z.string()),
    deletedAt: z.date().or(z.string()).nullable().optional().openapi({
      description: 'Soft-delete timestamp; null/omitted when active. Admin list only returns active configs.',
    }),
  })
  .openapi('Configuration');

// Configuration update request schema (for API) - only fields that can be updated
export const ConfigurationUpdateSchema = z.object({
  name: z.string().openapi({
    example: 'registration',
    description: 'Configuration name (e.g., "registration" or "deregistration")',
  }),
  vehicleCategoryId: z.string().openapi({
    example: 'vc_abc123xyz',
    description: 'ID of the vehicle category this configuration belongs to',
  }),
  labels: z
    .array(LabelSchema)
    .optional()
    .openapi({
      example: ['live'],
      description:
        'Labels that decide who can see and edit this configuration. Members need edit on every label. Omitted: [] on create, unchanged on update.',
    }),
  steps: z.array(PhotoStepSchema).openapi({
    description: 'Array of photo steps required for this vehicle category',
  }),
  expiryDuration: z.number().int().openapi({
    example: 180,
    description: 'Session expiry duration in minutes',
  }),
  ...completionMessageFields,
  strictVinCode: strictVinCodeField,
});

// Configuration create request schema - same fields as update (id and updatedAt are server-generated)
export const ConfigurationCreateSchema = ConfigurationUpdateSchema;

// Configuration ID parameter schema
export const ConfigurationIdParamSchema = z.object({
  id: z.string().openapi({
    param: { name: 'id', in: 'path' },
    example: 'conf_abc123xyz',
    description: 'Configuration ID',
  }),
});

// ============================================================================
// Type Exports
// ============================================================================

export type Configuration = z.infer<typeof ConfigurationSchema>;
export type ConfigurationUpdate = z.infer<typeof ConfigurationUpdateSchema>;
export type ConfigurationCreate = z.infer<typeof ConfigurationCreateSchema>;
